import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCsrfToken, fetchUser } from '../utils/auth.js';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Link } from 'react-router-dom';
import {
  X,
  Eye,
  EyeOff,
  ChevronDown,
  Users,
  Handshake,
  MessageCircle,
  Search,
  Sparkles,
  Newspaper,
  Radio,
  HelpCircle,
  Check,
} from 'lucide-react';
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLegend,
} from "../components/ui/field";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// ---------- Helper: load Google Identity Services script ----------
const loadGoogleScript = () => {
  return new Promise((resolve) => {
    if (window.google) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    document.head.appendChild(script);
  });
};

// ---------- Referral icon map (same as Next.js) ----------
const referralIcons = {
  friend: Users,
  admin_intro: Handshake,
  facebook: () => <i className="fab fa-facebook-f" />, // Font Awesome fallback
  instagram: () => <i className="fab fa-instagram" />,
  whatsapp: MessageCircle,
  tiktok: () => <i className="fab fa-tiktok" />,
  twitter: () => <i className="fab fa-twitter" />,
  google: Search,
  ai: Sparkles,
  poster: Newspaper,
  radio_tv: Radio,
  other: HelpCircle,
};

const referralLabels = {
  friend: 'Friend or Family',
  admin_intro: 'Admin Introduced Me',
  facebook: 'Facebook',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  tiktok: 'TikTok',
  twitter: 'X / Twitter',
  google: 'Google Search',
  ai: 'AI (Grok, ChatGPT, etc.)',
  poster: 'Poster',
  radio_tv: 'Radio / TV',
  other: 'Other',
};

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // ── Modal & form states ──
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [userType, setUserType] = useState(null);
  const [showUserTypeSelection, setShowUserTypeSelection] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [showReferralOptions, setShowReferralOptions] = useState(false);
  const [authContextMessage, setAuthContextMessage] = useState(null);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    notNumeric: false,
    notSimilar: false,
    match: false,
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [onboardingData, setOnboardingData] = useState({
    location: '',
    whatsapp_notification_number: '',
    how_did_you_hear: '',
    how_did_you_hear_other: '',
    user_type: '',
    business_name: '',
    service_provided: '',
    description: '',
    business_hours: '24/7',
  });
  const [onboardingErrors, setOnboardingErrors] = useState({});
  const [isOnboardingSubmitting, setIsOnboardingSubmitting] = useState(false);
  const [showOnboardingReferralOptions, setShowOnboardingReferralOptions] = useState(false);

  // ── Global user query ──
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user'],
    queryFn: fetchUser,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // ── Validation effect (original) ──
  useEffect(() => {
    const { username = '', password1 = '', password2 = '', location = '' } = formData;
    const newErrors = {};

    if (username.length < 3 || !/^[\w.@+\-\s]+$/.test(username)) {
      newErrors.username = 'At least 3 characters, letters/numbers/._- only';
    }
    if (!location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));

    const pwReqs = {
      length: password1.length >= 8,
      notNumeric: password1.length > 0 && !/^\d+$/.test(password1),
      notSimilar:
        password1.length > 0 &&
        !password1.toLowerCase().includes((username || '').toLowerCase()) &&
        !password1.toLowerCase().includes((formData.email || '').split('@')[0].toLowerCase()),
      match: password1 === password2 && password2 !== '',
    };
    setPasswordRequirements(pwReqs);

    const allPwValid = Object.values(pwReqs).every(Boolean);
    const noRelevantErrors = !newErrors.username && !newErrors.location;
    setIsFormValid(noRelevantErrors && allPwValid);
  }, [formData]);

  // ── Load Google script once ──
  useEffect(() => {
    loadGoogleScript();
  }, []);

  // ── Onboarding progress helper ──
  const calculateOnboardingProgress = () => {
    let steps = 3;
    let completed = 0;
    if (onboardingData.location.trim()) completed++;
    if (onboardingData.how_did_you_hear) completed++;
    if (onboardingData.user_type) completed++;
    if (onboardingData.user_type === 'provider') {
      steps = 4;
      if (onboardingData.business_name.trim() && onboardingData.service_provided.trim()) completed++;
    }
    return Math.min(100, Math.round((completed / steps) * 100));
  };

  // ── Close all modals and reset ──
  const closeModal = () => {
    setShowLogin(false);
    setShowRegister(false);
    setFormData({});
    setErrors({});
    setTermsAccepted(false);
    setUserType(null);
    setShowUserTypeSelection(true);
    setShowReferralOptions(false);
    setAuthContextMessage(null);
  };

  const openLogin = (contextMessage = null) => {
    closeModal();
    setAuthContextMessage(contextMessage);
    setShowLogin(true);
  };

  const openRegister = (contextMessage = null) => {
    closeModal();
    setAuthContextMessage(contextMessage);
    setShowRegister(true);
  };

  // ── Logout (with Google cleanup) ──
  const handleLogout = async () => {
    try {
      setIsAuthLoading(true);
      const csrfToken = await getCsrfToken();
      await axios.post(`${process.env.REACT_APP_API_URL}/api/logout/`, {}, {
        headers: { 'X-CSRFToken': csrfToken },
        withCredentials: true,
      });
      queryClient.removeQueries(['user']);

      // Clear Google session if exists
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
        window.google.accounts.id.disableAutoSelect();
      }

      navigate('/');
      toast.success('Logged out successfully.');
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to logout.');
    } finally {
      setIsAuthLoading(false);
    }
  };

  // ── Google Login Handler (mirrors Next.js) ──
  const handleGoogleLogin = useCallback(async () => {
    if (!window.google) {
      toast.error('Google sign-in is not ready. Please refresh the page.');
      return;
    }

    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      scope: 'email profile openid',
      callback: async (tokenResponse) => {
        if (tokenResponse.error) {
          toast.error(`Google sign-in failed: ${tokenResponse.error}`);
          return;
        }
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/auth/google/`,
            { credential: tokenResponse.access_token },
            { withCredentials: true }
          );
          queryClient.setQueryData(['user'], res.data.user);
          closeModal();
          toast.success(`Welcome, ${res.data.user.username}!`);

          const needsOnboarding = res.data.is_new_user || res.data.needs_onboarding;
          if (needsOnboarding) {
            setOnboardingData({
              location: '',
              whatsapp_notification_number: '',
              how_did_you_hear: '',
              how_did_you_hear_other: '',
              user_type: '',
              business_name: '',
              service_provided: '',
              description: '',
              business_hours: '24/7',
            });
            setOnboardingStep(1);
            setShowOnboarding(true);
          } else {
            // Restore pending booking if exists
            const pendingData = localStorage.getItem('pendingBooking');
            if (pendingData) {
              try {
                const booking = JSON.parse(pendingData);
                toast.success('Login successful! Restoring your order...');
                const targetUrl = booking.providerSlug
                  ? `/provider/${booking.providerSlug}`
                  : `/provider/${booking.providerId}`;
                navigate(targetUrl, { state: { restoreBooking: booking } });
                localStorage.removeItem('pendingBooking');
              } catch (e) {
                console.error('Failed to restore pending booking:', e);
              }
            }
          }
        } catch (err) {
          console.error('Google auth error:', err);
          toast.error(err.response?.data?.errors?.all?.[0] || 'Google authentication failed.');
        }
      },
    });

    client.requestAccessToken({ prompt: 'consent' });
  }, [queryClient, navigate, closeModal]);

  // ── Onboarding submission ──
  const handleOnboardingSubmit = async () => {
    const errors = {};
    if (!onboardingData.location.trim()) errors.location = 'Location is required';
    if (!onboardingData.how_did_you_hear) errors.how_did_you_hear = 'Please select an option';

    if (onboardingData.user_type === 'provider') {
      if (!onboardingData.business_name.trim()) errors.business_name = 'Business name is required';
      if (!onboardingData.service_provided.trim()) errors.service_provided = 'Service provided is required';
    }

    if (Object.keys(errors).length > 0) {
      setOnboardingErrors(errors);
      return;
    }

    setIsOnboardingSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('location', onboardingData.location);
      payload.append('whatsapp_notification_number', onboardingData.whatsapp_notification_number);
      payload.append('how_did_you_hear', onboardingData.how_did_you_hear);
      if (onboardingData.how_did_you_hear === 'other') {
        payload.append('how_did_you_hear_other', onboardingData.how_did_you_hear_other);
      }
      if (onboardingData.user_type === 'provider') {
        payload.append('user_type', 'provider');
        payload.append('business_name', onboardingData.business_name);
        payload.append('service_provided', onboardingData.service_provided);
        payload.append('description', onboardingData.description);
        payload.append('business_hours', onboardingData.business_hours);
      } else {
        payload.append('user_type', 'user');
      }

      const csrfToken = await getCsrfToken();
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/onboarding/`, payload, {
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });

      queryClient.setQueryData(['user'], res.data.user);
      setShowOnboarding(false);
      closeModal();
      toast.success('Profile completed! Welcome to SErraND.');
      navigate('/');
    } catch (err) {
      setOnboardingErrors(err.response?.data?.errors || { all: ['Failed to save profile.'] });
    } finally {
      setIsOnboardingSubmitting(false);
    }
  };

  // ── Register handler (unchanged logic, but closeModal after success) ──
  const handleRegister = async (e) => {
    e.preventDefault();

    if (!userType) {
      setErrors({ all: ['Please select a user type (Regular User or Service Provider).'] });
      setShowUserTypeSelection(true);
      return;
    }

    if (userType === 'provider') {
      if (!formData.service_provided) {
        setErrors({ service_provided: ['Service offered is required for providers.'] });
        return;
      }
      if (!formData.business_name) {
        setErrors({ business_name: ['Business name is required for providers.'] });
        return;
      }
    }

    if (!termsAccepted) {
      setErrors({ terms: ['You must agree to the Terms of Service and Privacy Policy.'] });
      return;
    }

    setIsAuthLoading(true);

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        data.append(key, value);
      }
    });
    data.append('user_type', userType);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/register/`, data, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      closeModal();
      setShowLogin(true);
      toast.success('Registration successful! Please log in.');
    } catch (error) {
      const errorData = error.response?.data?.errors || {
        all: ['Registration failed—please check your inputs or try again later.'],
      };
      setErrors(errorData);
    } finally {
      setIsAuthLoading(false);
    }
  };

  // ── Login handler (with pending booking restore) ──
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsAuthLoading(true);

    try {
      const csrfToken = await getCsrfToken();
      await axios.post(`${process.env.REACT_APP_API_URL}/api/login/`, formData, {
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });

      queryClient.invalidateQueries(['user']);
      closeModal();

      const pendingData = localStorage.getItem('pendingBooking');
      if (pendingData) {
        try {
          const booking = JSON.parse(pendingData);
          toast.success("Login successful! Restoring your order...");
          const targetUrl = booking.providerSlug
            ? `/provider/${booking.providerSlug}`
            : `/provider/${booking.providerId}`;
          navigate(targetUrl, { state: { restoreBooking: booking } });
          localStorage.removeItem('pendingBooking');
        } catch (e) {
          console.error("Failed to restore pending booking:", e);
          localStorage.removeItem('pendingBooking');
        }
      }
    } catch (error) {
      console.error('[Auth] Login Error:', error.response?.status);
      setErrors(error.response?.data?.errors || { all: ['Login failed'] });
    } finally {
      setIsAuthLoading(false);
    }
  };

  // ── Handlers for input changes ──
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
    setErrors((prev) => ({ ...prev, terms: undefined }));
  };

  const value = {
    user,
    userLoading,
    isAuthLoading,
    handleLogout,
    openLogin,
    openRegister,
    handleGoogleLogin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {/* ========== LOGIN MODAL (with Google button) ========== */}
      {showLogin && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-border">
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-foreground">
                  {authContextMessage?.title || 'Welcome Back! 👋'}
                </h2>
                <button onClick={closeModal} className="text-muted-foreground hover:text-foreground">
                  <X className="w-6 h-6" />
                </button>
              </div>
              {authContextMessage?.subtitle && (
                <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl">
                  <p className="text-foreground font-medium text-sm leading-relaxed">
                    {authContextMessage.subtitle}
                  </p>
                </div>
              )}

              {/* Google Sign In */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="flex items-center justify-center w-full border rounded-md py-2 px-4 hover:bg-accent transition-colors mb-4"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </button>

              <div className="flex items-center mb-6">
                <hr className="flex-1" />
                <span className="px-2 text-xs text-muted-foreground">or</span>
                <hr className="flex-1" />
              </div>

              <form onSubmit={handleLogin} className="space-y-6">
                {/* same as before */}
                <FieldGroup>
                  <Field orientation="vertical">
                    <FieldLabel htmlFor="username_or_email">Username or Email</FieldLabel>
                    <FieldContent>
                      <Input
                        id="username_or_email"
                        name="username_or_email"
                        onChange={handleInputChange}
                        placeholder="Enter username or email"
                        required
                      />
                    </FieldContent>
                  </Field>
                  <Field orientation="vertical">
                    <FieldLabel htmlFor="password">Password</FieldLabel>
                    <FieldContent>
                      <div className="relative">
                        <Input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          onChange={handleInputChange}
                          placeholder="Enter your password"
                          className="pr-12"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </FieldContent>
                  </Field>
                </FieldGroup>
                {errors.all && <FieldError>{errors.all[0]}</FieldError>}
                <div className="flex justify-between items-center text-sm">
                  <Link to="/forgot-password" className="text-primary hover:underline">Forgot Password?</Link>
                </div>
                <div className="flex gap-3">
                  <Button type="submit" disabled={isAuthLoading} className="flex-1" size="lg">
                    {isAuthLoading ? 'Logging in...' : 'Login'}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeModal} size="lg">Cancel</Button>
                </div>
                <div className="text-center pt-4 border-t">
                  <p className="text-muted-foreground text-sm">
                    Don't have an account?{' '}
                    <button type="button" onClick={() => { setShowLogin(false); setShowRegister(true); }} className="text-primary hover:underline">Register here</button>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ========== ONBOARDING MODAL (mirrors Next.js) ========== */}
      {showOnboarding && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-border">
            <div className="p-6 md:p-8">
              <h2 className="text-lg font-bold text-foreground mb-2">Complete Your Profile</h2>
              <p className="text-xs text-muted-foreground mb-4">A complete profile builds trust and helps you get the most out of SErraND.</p>
              <div className="w-full bg-muted rounded-full h-1.5 mb-6">
                <div className="bg-emerald-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${calculateOnboardingProgress()}%` }} />
              </div>
              <p className="text-[10px] text-muted-foreground text-right -mt-4 mb-4">{calculateOnboardingProgress()}% complete</p>

              {/* Step 1: Location, WhatsApp, Referral */}
              {onboardingStep === 1 && (
                <div className="space-y-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold">1</span>
                    <span className="text-sm font-medium">Where are you based?</span>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium">Location *</label>
                    <Input
                      placeholder="e.g., Umoja, Kasarani"
                      value={onboardingData.location}
                      onChange={(e) => setOnboardingData(prev => ({ ...prev, location: e.target.value }))}
                      className="text-xs"
                    />
                    {onboardingErrors.location && <p className="text-[10px] text-red-400">{onboardingErrors.location}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium">WhatsApp Number (recommended, for updates)</label>
                    <div className="flex border border-input rounded-lg overflow-hidden">
                      <span className="bg-muted px-3 py-2 text-xs text-muted-foreground border-r">+254</span>
                      <Input
                        type="tel"
                        placeholder="712345678"
                        value={(onboardingData.whatsapp_notification_number || '').replace('+254', '')}
                        onChange={(e) => {
                          const num = e.target.value.replace(/\D/g, '').slice(0, 9);
                          setOnboardingData(prev => ({ ...prev, whatsapp_notification_number: num ? '+254' + num : '' }));
                        }}
                        className="flex-1 border-none focus:ring-0 text-xs"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium">How did you hear about us? *</label>
                    <button
                      type="button"
                      onClick={() => setShowOnboardingReferralOptions(!showOnboardingReferralOptions)}
                      className="w-full px-3 py-2 bg-muted border border-input rounded-lg text-left flex items-center justify-between text-xs"
                    >
                      <span>{onboardingData.how_did_you_hear ? referralLabels[onboardingData.how_did_you_hear] : 'Tap to select...'}</span>
                      <ChevronDown className="h-4 w-4 transition-transform" />
                    </button>
                    {showOnboardingReferralOptions && (
                      <div className="mt-2 border border-input rounded-lg p-2 bg-muted/50 max-h-40 overflow-y-auto grid grid-cols-1 gap-1">
                        {Object.keys(referralLabels).map(value => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => {
                              setOnboardingData(prev => ({ ...prev, how_did_you_hear: value }));
                              setShowOnboardingReferralOptions(false);
                            }}
                            className="flex items-center gap-2 p-2 rounded-md hover:bg-accent text-xs text-left"
                          >
                            {typeof referralIcons[value] === 'function' ? referralIcons[value]() : React.createElement(referralIcons[value] || HelpCircle, { className: 'w-4 h-4 text-muted-foreground' })}
                            <span>{referralLabels[value]}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {onboardingData.how_did_you_hear === 'other' && (
                      <Input
                        placeholder="Specify..."
                        value={onboardingData.how_did_you_hear_other}
                        onChange={(e) => setOnboardingData(prev => ({ ...prev, how_did_you_hear_other: e.target.value }))}
                        className="mt-2 text-xs"
                      />
                    )}
                    {onboardingErrors.how_did_you_hear && <p className="text-[10px] text-red-400">{onboardingErrors.how_did_you_hear}</p>}
                  </div>
                  <Button onClick={() => {
                    const errs = {};
                    if (!onboardingData.location.trim()) errs.location = 'Required';
                    if (!onboardingData.how_did_you_hear) errs.how_did_you_hear = 'Please select';
                    if (Object.keys(errs).length) {
                      setOnboardingErrors(errs);
                      return;
                    }
                    setOnboardingErrors({});
                    setOnboardingStep(2);
                  }} className="w-full">Continue</Button>
                </div>
              )}

              {/* Step 2: Account type */}
              {onboardingStep === 2 && (
                <div className="space-y-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold">2</span>
                    <span className="text-sm font-medium">How would you like to use SErraND?</span>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        setOnboardingData(prev => ({ ...prev, user_type: 'user' }));
                        handleOnboardingSubmit();
                      }}
                      className="p-4 border rounded-xl text-left hover:bg-muted/50"
                    >
                      <div className="font-semibold text-sm">I'm here to find & hire services</div>
                      <div className="text-xs text-muted-foreground mt-1">Browse verified providers, send requests, manage jobs.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setOnboardingData(prev => ({ ...prev, user_type: 'provider' }));
                        setOnboardingStep(3);
                      }}
                      className="p-4 border rounded-xl text-left hover:bg-muted/50"
                    >
                      <div className="font-semibold text-sm">I want to offer my services</div>
                      <div className="text-xs text-muted-foreground mt-1">Create your business profile and start receiving client requests.</div>
                    </button>
                  </div>
                  <Button variant="ghost" className="text-xs" onClick={() => setOnboardingStep(1)}>Back</Button>
                </div>
              )}

              {/* Step 3: Provider details */}
              {onboardingStep === 3 && (
                <div className="space-y-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold">3</span>
                    <span className="text-sm font-medium">Set up your business profile</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Complete profiles receive significantly more inquiries.</p>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium">Business Name *</label>
                    <Input
                      value={onboardingData.business_name}
                      onChange={(e) => setOnboardingData(prev => ({ ...prev, business_name: e.target.value }))}
                      className="text-xs"
                    />
                    {onboardingErrors.business_name && <p className="text-[10px] text-red-400">{onboardingErrors.business_name}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium">Service Provided *</label>
                    <Input
                      value={onboardingData.service_provided}
                      onChange={(e) => setOnboardingData(prev => ({ ...prev, service_provided: e.target.value }))}
                      className="text-xs"
                    />
                    {onboardingErrors.service_provided && <p className="text-[10px] text-red-400">{onboardingErrors.service_provided}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium">Description</label>
                    <Textarea
                      value={onboardingData.description}
                      onChange={(e) => setOnboardingData(prev => ({ ...prev, description: e.target.value }))}
                      className="text-xs min-h-[80px]"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium">Work Hours</label>
                    <Input
                      value={onboardingData.business_hours}
                      onChange={(e) => setOnboardingData(prev => ({ ...prev, business_hours: e.target.value }))}
                      className="text-xs"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button variant="outline" className="flex-1 text-xs" onClick={() => setOnboardingStep(2)}>Back</Button>
                    <Button className="flex-1 text-xs" onClick={handleOnboardingSubmit} disabled={isOnboardingSubmitting}>
                      {isOnboardingSubmitting ? 'Saving...' : 'Complete Profile'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========== REGISTER MODAL (with Google button in type selection) ========== */}
      {showRegister && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-border">
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-foreground">Join SErraND</h2>
                <button onClick={closeModal} className="text-muted-foreground hover:text-foreground">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {showUserTypeSelection ? (
                <div className="space-y-6">
                  <p className="text-muted-foreground text-base text-center">Are you looking for services or offering them?</p>
                  <div className="grid grid-cols-1 gap-4">
                    <Button variant="default" className="h-auto py-4 px-5 text-left" onClick={() => { setUserType('user'); setShowUserTypeSelection(false); }}>
                      <div><div className="font-semibold">Join as Customer</div><div className="text-xs text-white/70">Looking for help with errands or tasks</div></div>
                    </Button>
                    <Button variant="outline" className="h-auto py-4 px-5 text-left" onClick={() => { setUserType('provider'); setShowUserTypeSelection(false); }}>
                      <div><div className="font-semibold">Join as Service Provider</div><div className="text-xs text-muted-foreground">Offering services like plumbing, cleaning...</div></div>
                    </Button>
                  </div>

                  {/* Google Sign Up */}
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    className="flex items-center justify-center w-full border rounded-md py-2 px-4 hover:bg-accent transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Sign up with Google
                  </button>

                  <div className="flex items-center my-4"><hr className="flex-1" /><span className="px-2 text-xs text-muted-foreground">or</span><hr className="flex-1" /></div>

                  <div className="text-center pt-4">
                    <p className="text-muted-foreground text-sm">Already have an account? <button type="button" onClick={() => { setShowRegister(false); setShowLogin(true); }} className="text-primary hover:underline">Login here</button></p>
                  </div>
                </div>
              ) : (
                /* Same registration form as before, but keep all fields */
                <form onSubmit={handleRegister} className="space-y-6">
                  <div className="text-sm text-muted-foreground mb-4">
                    Registering as: <span className="font-semibold text-foreground">{userType === 'user' ? 'Regular User' : 'Service Provider'}</span>{' '}
                    <button type="button" onClick={() => setShowUserTypeSelection(true)} className="text-primary hover:underline text-sm ml-1">Change</button>
                  </div>
                  <FieldGroup>
                    <Field orientation="vertical"><FieldLabel>Username (min 3 chars)</FieldLabel><Input name="username" value={formData.username || ''} onChange={handleInputChange} required />{errors.username && <FieldError>{errors.username}</FieldError>}</Field>
                    <Field orientation="vertical"><FieldLabel>Email</FieldLabel><Input name="email" type="email" value={formData.email || ''} onChange={handleInputChange} required /></Field>
                    <Field orientation="vertical"><FieldLabel>Password</FieldLabel><div className="relative"><Input name="password1" type={showPassword1 ? 'text' : 'password'} value={formData.password1 || ''} onChange={handleInputChange} className="pr-12" required /><button type="button" onClick={() => setShowPassword1(!showPassword1)} className="absolute right-3 top-1/2">{showPassword1 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button></div></Field>
                    <Field orientation="vertical"><FieldLabel>Confirm Password</FieldLabel><div className="relative"><Input name="password2" type={showPassword2 ? 'text' : 'password'} value={formData.password2 || ''} onChange={handleInputChange} className="pr-12" required /><button type="button" onClick={() => setShowPassword2(!showPassword2)} className="absolute right-3 top-1/2">{showPassword2 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button></div></Field>
                    <FieldDescription>
                      <ul className="text-sm space-y-1 mt-2">
                        <li className="flex items-center gap-2">{passwordRequirements.length ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />} At least 8 characters</li>
                        <li className="flex items-center gap-2">{passwordRequirements.notNumeric ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />} Not entirely numbers</li>
                        <li className="flex items-center gap-2">{passwordRequirements.notSimilar ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />} Not too similar to username or email</li>
                        <li className="flex items-center gap-2">{passwordRequirements.match ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />} Passwords match</li>
                      </ul>
                    </FieldDescription>
                    <Field orientation="vertical"><FieldLabel>Location (e.g., Yukos Kitengela)</FieldLabel><Input name="location" value={formData.location || ''} onChange={handleInputChange} required />{errors.location && <FieldError>{errors.location}</FieldError>}</Field>
                  </FieldGroup>
                  <Field orientation="vertical"><FieldLabel>WhatsApp Number (for order updates – recommended)</FieldLabel><div className="flex border border-input rounded-lg overflow-hidden"><div className="bg-muted px-4 py-3 text-muted-foreground font-medium border-r"><i className="fab fa-whatsapp text-green-500" /> +254</div><Input name="whatsapp_notification_number" type="tel" placeholder="712345678" value={(formData.whatsapp_notification_number || '').replace('+254', '')} onChange={(e) => { let num = e.target.value.replace(/\D/g, '').slice(0, 9); handleInputChange({ target: { name: 'whatsapp_notification_number', value: num ? '+254' + num : '' } }); }} className="flex-1 border-none" /></div></Field>
                  <Field orientation="vertical"><FieldLabel>How did you hear about SErraND?</FieldLabel><button type="button" onClick={() => setShowReferralOptions(!showReferralOptions)} className="w-full px-4 py-3 bg-muted border border-input rounded-lg text-left flex items-center justify-between"><span>{formData.how_did_you_hear ? referralLabels[formData.how_did_you_hear] : 'Tap to select...'}</span><ChevronDown className="h-4 w-4" /></button>{showReferralOptions && (<div className="mt-2 border border-input rounded-lg p-3 bg-muted/50 max-h-64 overflow-y-auto grid grid-cols-1 gap-2">{Object.keys(referralLabels).map(value => (<button key={value} type="button" onClick={() => { setFormData(prev => ({ ...prev, how_did_you_hear: value, how_did_you_hear_other: value === 'other' ? prev.how_did_you_hear_other : '' })); setShowReferralOptions(false); }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent text-left w-full">{typeof referralIcons[value] === 'function' ? referralIcons[value]() : React.createElement(referralIcons[value] || HelpCircle, { className: 'w-5 h-5 text-muted-foreground' })}<span className="text-sm">{referralLabels[value]}</span></button>))}</div>)}{formData.how_did_you_hear === 'other' && (<Input name="how_did_you_hear_other" placeholder="e.g. YouTube, Church, School..." value={formData.how_did_you_hear_other || ''} onChange={handleInputChange} className="mt-3" />)}</Field>
                  {userType === 'provider' && (<FieldGroup className="p-6 bg-muted/50 rounded-xl border border-border space-y-6"><FieldLegend>Provider Details</FieldLegend><Field orientation="vertical"><FieldLabel>Brand Name</FieldLabel><Input name="business_name" value={formData.business_name || ''} onChange={handleInputChange} required />{errors.business_name && <FieldError>{errors.business_name}</FieldError>}</Field><Field orientation="vertical"><FieldLabel>Service Provided</FieldLabel><Input name="service_provided" value={formData.service_provided || ''} onChange={handleInputChange} required />{errors.service_provided && <FieldError>{errors.service_provided[0]}</FieldError>}</Field><Field orientation="vertical"><FieldLabel>Description</FieldLabel><textarea name="description" value={formData.description || ''} placeholder="Description - DO NOT input personal contact info" className="w-full px-4 py-3 bg-background border border-input rounded-lg text-sm focus:ring-2 focus:ring-ring min-h-[100px]" onChange={handleInputChange} /></Field><Field orientation="vertical"><FieldLabel>Work Hours</FieldLabel><Input name="business_hours" value={formData.business_hours || ''} onChange={handleInputChange} placeholder="e.g., 24/7, Mon-Sat 8am-6pm" /></Field></FieldGroup>)}
                  <Field><div className="flex items-start space-x-3"><input type="checkbox" id="terms" checked={termsAccepted} onChange={handleTermsChange} className="mt-1 h-4 w-4 text-primary border-input rounded" required /><label htmlFor="terms" className="text-sm text-muted-foreground">I agree to the <Link to="/terms" target="_blank" className="text-primary hover:underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="text-primary hover:underline">Privacy Policy</Link></label></div>{errors.terms && <FieldError>{errors.terms}</FieldError>}</Field>
                  {errors.all && <FieldError>{errors.all[0]}</FieldError>}
                  {Object.entries(errors).map(([key, value]) => { if (key === 'all' || key === 'service_provided' || key === 'terms' || !Array.isArray(value)) return null; return <FieldError key={key}>{value[0]}</FieldError>; })}
                  <div className="flex gap-3 pt-4"><Button type="submit" disabled={isAuthLoading} className="flex-1" size="lg">{isAuthLoading ? 'Registering...' : 'Register'}</Button><Button type="button" variant="outline" onClick={closeModal} size="lg">Cancel</Button></div>
                  <div className="text-center pt-4 border-t"><p className="text-sm text-muted-foreground">Already have an account? <button type="button" onClick={() => { setShowRegister(false); setShowLogin(true); }} className="text-primary hover:underline font-medium">Login here</button></p></div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}
